import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import Seo from "../components/Seo";
import { api, imageUrl, formatApiError } from "../lib/api";
import {
  ArrowUpRight,
  ArrowLeft,
  Star,
  Phone,
  Envelope,
} from "@phosphor-icons/react";

export default function OfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/offers/${id}`)
      .then((r) => setOffer(r.data))
      .catch((e) => {
        if (e?.response?.status === 410) {
          setExpired(true);
          setError("This offer has ended");
        } else {
          setError(formatApiError(e));
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <PublicLayout>
        <Seo title="Loading offer…" path={`/offers/${id}`} />
        <div className="py-32 text-center font-mono-accent text-[#a8b2cc]">Loading…</div>
      </PublicLayout>
    );
  }

  if (error || !offer) {
    return (
      <PublicLayout>
        <Seo title={expired ? "Offer ended" : "Offer not found"} path={`/offers/${id}`} />
        <div className="py-32 text-center max-w-xl mx-auto px-6">
          <div className="font-display text-4xl mb-4 text-[#f5ecd9]" data-testid="offer-not-available">
            {expired ? "This offer has ended" : "Offer not found"}
          </div>
          <p className="text-[#a8b2cc] mb-8 text-lg">
            {expired
              ? "This deal is no longer available — but we have plenty more like it. Browse the latest specials or build a custom package."
              : "This offer may have ended. Browse the latest deals or build a custom package."}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button onClick={() => navigate("/offers")} className="btn-primary" data-testid="offer-not-found-back">
              All Offers <ArrowUpRight size={14} weight="bold" />
            </button>
            <Link to="/build-package" className="btn-outline">
              Build a Package
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const enquireQuery = new URLSearchParams({
    offer: offer.title,
    provider: offer.provider,
  }).toString();

  return (
    <PublicLayout>
      <Seo
        title={`${offer.title} (${offer.provider})`}
        description={offer.description.slice(0, 160)}
        path={`/offers/${offer.id}`}
        image={offer.image_path ? imageUrl(offer.image_path) : "/hero-rainbow.jpg"}
      />

      {/* Hero */}
      <section className="relative pt-16 pb-12 overflow-hidden text-center" data-testid="offer-detail-page">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[80vw] h-[260px] rainbow-swoosh pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-6">
          <Link to="/offers" className="inline-flex items-center gap-2 eyebrow text-[#a8b2cc] hover:text-[#f5ecd9] mb-8" data-testid="offer-back-link">
            <ArrowLeft size={12} weight="bold" /> All Offers
          </Link>
          <div className="flex justify-center mb-5">
            <Star size={22} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="eyebrow mb-4">{offer.provider}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-[4.4rem] leading-[0.98] max-w-3xl mx-auto" data-testid="offer-detail-title">
            {offer.title}
          </h1>
          {offer.badge && (
            <div className="mt-6">
              <span className="brand-chip bg-[#af3fa6] text-white">{offer.badge}</span>
            </div>
          )}
          {offer.price && (
            <div className="mt-6 font-display text-3xl rainbow-text" data-testid="offer-detail-price">
              {offer.price}
            </div>
          )}
        </div>
      </section>

      <div className="rainbow-bar rainbow-bar-xl" />

      {/* Body */}
      <section className="py-16">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            {offer.image_path && (
              <div className="rounded-[28px] overflow-hidden border border-[#2c3854]">
                <img
                  src={imageUrl(offer.image_path)}
                  alt={offer.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            )}
            <article className="soft-card p-8 lg:p-10">
              <div className="eyebrow mb-4">Deal Details</div>
              <p className="text-[#f5ecd9] leading-relaxed text-lg whitespace-pre-line" data-testid="offer-detail-description">
                {offer.description}
              </p>
            </article>
          </div>

          {/* Side enquiry CTA */}
          <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start space-y-5">
            <div className="soft-card p-8 bg-[#141e32]">
              <div className="eyebrow mb-3">Interested?</div>
              <h2 className="font-display text-3xl text-[#f5ecd9] leading-tight mb-5">
                Enquire about this <span className="rainbow-text">deal</span>.
              </h2>
              <p className="text-[#a8b2cc] mb-7 leading-relaxed">
                Click below — we'll prefill the form so you only need to add your name and email.
                Most enquiries are answered within one business day.
              </p>
              <Link
                to={`/contact?${enquireQuery}`}
                className="btn-primary w-full justify-center"
                data-testid="offer-enquire-button"
              >
                Enquire about this deal <ArrowUpRight size={14} weight="bold" />
              </Link>
              <div className="mt-6 pt-6 border-t border-[#2c3854] space-y-3">
                <a
                  href="tel:0718889055"
                  className="flex items-center gap-3 text-[#f5ecd9] hover:text-[#dda632] transition-colors"
                  data-testid="offer-phone-link"
                >
                  <Phone size={18} weight="duotone" className="text-[#af3fa6]" />
                  <span className="font-display text-xl">071 888 9055</span>
                </a>
                <a
                  href="mailto:info@infinity-dms.co.za"
                  className="flex items-center gap-3 text-[#f5ecd9] hover:text-[#dda632] transition-colors"
                  data-testid="offer-email-link"
                >
                  <Envelope size={18} weight="duotone" className="text-[#af3fa6]" />
                  <span className="font-display text-base break-all">info@infinity-dms.co.za</span>
                </a>
              </div>
            </div>

            <div className="text-center">
              <Link to="/build-package" className="btn-link" data-testid="offer-build-package-link">
                Or build a custom package <ArrowUpRight size={12} weight="bold" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}
